import React from 'react';
interface ApiOverviewPageProps {
  onNavigate: (id: string) => void;
}
export function ApiOverviewPage({
  onNavigate
}: ApiOverviewPageProps) {
  const categories = [{
    id: 'api-agreement',
    title: 'Agreement',
    desc: "Allows the merchant to store the payer's payment details and make payments under that agreement without the payer's involvement."
  }, {
    id: 'api-authentication',
    title: 'Authentication',
    desc: 'Authentication process to confirm the identity of the payer attempting to make a transaction.'
  }, {
    id: 'api-batch',
    title: 'Batch',
    desc: 'Submit batches of operations (Captures, Refunds, etc) to the Gateway for processing without direct payer interaction.'
  }, {
    id: 'api-browser-payment',
    title: 'Browser Payment',
    desc: "Browser payment methods allow a payer to pay for goods and services online on the browser payment provider's website."
  }, {
    id: 'api-gateway',
    title: 'Gateway',
    desc: 'Operations related to speaking directly to the Gateway for status or payment option related details.'
  }, {
    id: 'api-hosted-checkout',
    title: 'Hosted Checkout',
    desc: 'Request to initiate a Hosted Checkout interaction.'
  }, {
    id: 'api-payment-plan',
    title: 'Payment Plan',
    desc: 'Operations related to managing Payment Plans.'
  }, {
    id: 'api-session',
    title: 'Session',
    desc: 'Operations related to managing Sessions.'
  }, {
    id: 'api-tokenization',
    title: 'Tokenization',
    desc: 'Operations related to managing Tokens.'
  }, {
    id: 'api-transaction',
    title: 'Transaction',
    desc: 'Operations related to processing payments.'
  }];
  return <div className="max-w-6xl mx-auto px-8 py-12" data-id="element-892">
      <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-893">
        <div className="flex-1 max-w-2xl" data-id="element-894">
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 leading-tight mb-6" data-id="element-895">
            Gateway API Reference Documentation
          </h1>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-896">
            Ready to redefine your payment experience? Join Gateway today and
            unlock a world where getting paid is not a hurdle but a seamless,
            simple process.
          </p>
          <p className="text-gray-700 leading-relaxed" data-id="element-897">
            From API version 100, integrations must be able to handle{' '}
            <span className="text-[#0B5FA5] font-medium" data-id="element-898">non-breaking</span>{' '}
            changes. Any API References for deprecated versions are hidden. For
            information on all API versions, see our{' '}
            <a href="#" className="text-[#0B5FA5] hover:underline font-medium" data-id="element-899">
              changelog
            </a>
            .
          </p>
        </div>

        {/* Abstract Cubes Illustration - Updated to Paysys Blue */}
        <div className="hidden md:block w-64 h-64 relative" data-id="element-900">
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-901">
            <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-902">
              {/* Top cube */}
              <path d="M100 20 L130 35 L100 50 L70 35 Z" fill="#60A5FA" data-id="element-903" />
              <path d="M70 35 L100 50 L100 85 L70 70 Z" fill="#0B5FA5" data-id="element-904" />
              <path d="M130 35 L130 70 L100 85 L100 50 Z" fill="#1E73BE" data-id="element-905" />

              {/* Left cube */}
              <path d="M60 80 L90 95 L60 110 L30 95 Z" fill="#60A5FA" data-id="element-906" />
              <path d="M30 95 L60 110 L60 145 L30 130 Z" fill="#0B5FA5" data-id="element-907" />
              <path d="M90 95 L90 130 L60 145 L60 110 Z" fill="#1E73BE" data-id="element-908" />

              {/* Right cube */}
              <path d="M140 80 L170 95 L140 110 L110 95 Z" fill="#60A5FA" data-id="element-909" />
              <path d="M110 95 L140 110 L140 145 L110 130 Z" fill="#0B5FA5" data-id="element-910" />
              <path d="M170 95 L170 130 L140 145 L140 110 Z" fill="#1E73BE" data-id="element-911" />

              {/* Bottom cube */}
              <path d="M100 140 L130 155 L100 170 L70 155 Z" fill="#F5C400" data-id="element-912" />
              <path d="M70 155 L100 170 L100 205 L70 190 Z" fill="#D97706" data-id="element-913" />
              <path d="M130 155 L130 190 L100 205 L100 170 Z" fill="#B45309" data-id="element-914" />

              {/* Connecting lines */}
              <line x1="100" y1="85" x2="60" y2="110" stroke="#E5E7EB" strokeWidth="2" data-id="element-915" />
              <line x1="100" y1="85" x2="140" y2="110" stroke="#E5E7EB" strokeWidth="2" data-id="element-916" />
              <line x1="60" y1="145" x2="100" y2="170" stroke="#E5E7EB" strokeWidth="2" data-id="element-917" />
              <line x1="140" y1="145" x2="100" y2="170" stroke="#E5E7EB" strokeWidth="2" data-id="element-918" />
            </svg>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-12" data-id="element-919">
        <h2 className="text-2xl font-medium text-gray-900 mb-4" data-id="element-920">
          Understanding the Gateway API
        </h2>
        <p className="text-gray-700 mb-10" data-id="element-921">
          Whether you're a coding maestro or just starting out, our intuitive
          interface and robust API have been meticulously crafted to ensure that
          integrating payments into your applications is not only efficient but
          downright enjoyable.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10" data-id="element-922">
          {categories.map(cat => <div key={cat.id} className="flex flex-col p-6 rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all bg-white" data-id="element-923">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-924">
                {cat.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-925">{cat.desc}</p>
              <button onClick={() => onNavigate(cat.id)} className="text-sm text-[#0B5FA5] hover:text-[#1E73BE] text-left font-semibold flex items-center" data-id="element-926">
                View Documentation <span className="ml-1" data-id="element-927">→</span>
              </button>
            </div>)}
        </div>
      </div>
    </div>;
}